import { useState } from 'react';
import { authApi, ApiError, LoginData } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertCircle, Loader2 } from 'lucide-react';

interface LoginFormProps {
  onLoginSuccess: (token: string, username: string) => void;
  onSwitchToSignup: () => void;
  onSwitchToConfirm: () => void;
}

export const LoginForm = ({ onLoginSuccess, onSwitchToSignup, onSwitchToConfirm }: LoginFormProps) => {
  const [loginData, setLoginData] = useState<LoginData>({
    username: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!loginData.username || !loginData.password) {
      setError('Please enter username and password');
      return;
    }

    setIsLoading(true);

    try {
      const response = await authApi.login(loginData);

      if (response.id_token === 'none') {
        setError('Account suspended due to non-payment. Please contact support@patentloop.com');
        setIsLoading(false);
        return;
      }

      onLoginSuccess(response.id_token, response.username);
    } catch (err) {
      const error = err as ApiError;

      // Check for specific error types
      if (error.status === 402) {
        setError('Please complete payment with Stripe or contact support@patentloop.com');
      } else if (error.status === 403) {
        setError('Username or password incorrect');
      } else if (error.error && typeof error.error === 'string') {
        if (error.error.includes('UserNotConfirmedException')) {
          setError('Please check your email and confirm your account before logging in');
        } else if (error.error.includes('NotAuthorizedException') || error.error.includes('Incorrect username or password')) {
          setError('Username or password incorrect');
        } else {
          setError('Login failed. Please try again');
        }
      } else {
        setError(error.message || 'Login failed. Please try again');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!loginData.username) {
      setError('Please enter your username first');
      return;
    }

    setError('');
    setShowForgotPassword(true);

    try {
      await authApi.forgotPassword(loginData.username);
      setResetSuccess(true);
    } catch (err) {
      setError('Failed to send verification code. Please try again');
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!loginData.username || !verificationCode || !newPassword) {
      setError('Please fill in all fields');
      return;
    }

    setResetLoading(true);

    try {
      await authApi.confirmNewPassword(loginData.username, verificationCode, newPassword);
      setError('');
      setShowForgotPassword(false);
      setResetSuccess(false);
      setVerificationCode('');
      setNewPassword('');
      alert('Password reset successful! Please log in with your new password.');
    } catch (err) {
      const error = err as ApiError;
      setError(error.message || 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto animate-slide-up">
      <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 hover:shadow-3xl hover:border-white/30 transition-all duration-300 relative overflow-hidden group">
        {/* Animated border gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"></div>

        {/* Shimmer effect */}
        <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>

        <div className="text-center mb-6 relative z-10">
          <h2 className="text-3xl font-bold text-white mb-2 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
            Welcome Back
          </h2>
          <p className="text-white/80 text-sm">Sign in to continue to Patent Loop</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-2 text-red-200 text-sm animate-shake backdrop-blur-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0 animate-pulse" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={showForgotPassword ? handleResetPassword : handleLogin} className="space-y-4 relative z-10">
          <div className="transform transition-all duration-300 hover:scale-[1.02]">
            <Input
              type="text"
              name="username"
              placeholder="Username"
              value={loginData.username}
              onChange={handleChange}
              className="bg-white/90 border-white/30 text-gray-900 placeholder:text-gray-500 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-400/50 transition-all duration-200"
            />
          </div>

          {!showForgotPassword ? (
            <div className="transform transition-all duration-300 hover:scale-[1.02]">
              <Input
                type="password"
                name="password"
                placeholder="Password"
                value={loginData.password}
                onChange={handleChange}
                className="bg-white/90 border-white/30 text-gray-900 placeholder:text-gray-500 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-400/50 transition-all duration-200"
              />
            </div>
          ) : (
            <>
              <div className="transform transition-all duration-300 hover:scale-[1.02]">
                <Input
                  type="text"
                  placeholder="Verification Code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  className="bg-white/90 border-white/30 text-gray-900 placeholder:text-gray-500 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-400/50 transition-all duration-200"
                />
              </div>
              <div className="transform transition-all duration-300 hover:scale-[1.02]">
                <Input
                  type="password"
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-white/90 border-white/30 text-gray-900 placeholder:text-gray-500 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-400/50 transition-all duration-200"
                />
              </div>
              {resetSuccess && (
                <p className="text-green-300 text-sm">
                  Verification code sent to your email
                </p>
              )}
            </>
          )}

          <Button
            type="submit"
            disabled={isLoading || resetLoading}
            className="w-full bg-gradient-to-r from-blue-600 via-blue-700 to-purple-600 hover:from-blue-700 hover:via-purple-700 hover:to-pink-600 text-white font-semibold py-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300 relative overflow-hidden group"
          >
            <span className="relative z-10">
              {(isLoading || resetLoading) ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                  {showForgotPassword ? 'Resetting...' : 'Logging in...'}
                </>
              ) : (
                <>{showForgotPassword ? 'Reset Password' : 'Login'}</>
              )}
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
          </Button>

          {!showForgotPassword && (
            <>
              <Button
                type="button"
                variant="ghost"
                onClick={handleForgotPassword}
                className="w-full text-white/90 hover:text-white hover:bg-white/10 transform hover:scale-[1.02] transition-all duration-200"
              >
                Forgot password?
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={onSwitchToConfirm}
                className="w-full text-white/90 hover:text-white hover:bg-white/10 transform hover:scale-[1.02] transition-all duration-200"
              >
                Enter Confirmation Code
              </Button>
            </>
          )}

          {showForgotPassword && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setShowForgotPassword(false);
                setResetSuccess(false);
                setError('');
              }}
              className="w-full text-white/90 hover:text-white hover:bg-white/10 transform hover:scale-[1.02] transition-all duration-200"
            >
              Back to Login
            </Button>
          )}
        </form>

        <div className="mt-6 flex flex-col gap-3 relative z-10">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/30 to-blue-500/30 rounded-xl blur-sm"></div>
            <button
              onClick={onSwitchToSignup}
              className="relative w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2 group"
            >
              <span>Create Free Account</span>
              <span className="inline-block transform group-hover:translate-x-1 transition-transform duration-200">→</span>
            </button>
          </div>
          <p className="text-white/60 text-xs text-center">
            50 Free Credits • No Credit Card Required
          </p>
        </div>
      </div>
    </div>
  );
};
